import { useContext, useMemo, useState } from "react";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { useTranslation } from "react-i18next";
import { Typography, Tooltip } from "@mui/material";
import { useStyles } from "./useDetailsReportColumns.styles";
import {
    DetailsReportColumnsIdEnum,
    IDetailsReportRow,
    IExpandedDowntimeDetailsRow,
} from "./useDetailsReportColumnsInterfaces";
import moment from "moment";
import { getDowntimeDurationInHoursMinutes, truncateLongText } from "utilities/utilities";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable, FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import {
    FunctionalLocationsDropdown,
    FailureTypeDetailsDropdown,
    FailureCodeDetailsDropdown,
    OperatorDropdown,
    NoteTextfield,
    EditionOptionsCell,
} from "../components";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { AreasDropdown } from "../components/AreasDropdown";

const cellStyle = { padding: "16px 8px" };

interface IDetailsReportColumns extends Omit<IMainTableColumn, "render" | "renderExpandedRow"> {
    render: (row: IDetailsReportRow) => string | number | JSX.Element | null | undefined;
    renderExpandedRow?: (row: IExpandedDowntimeDetailsRow) => string | JSX.Element | number;
}

export const useDetailsReportColumns = (
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
): IDetailsReportColumns[] => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const commentLengthLimit: number = 75;
    const { millTimezone } = useContext(LocationsContext);

    const [editionMode, setEditioMode] = useState<
        {
            id: number;
        }[]
    >([]);

    const { defaultFailureTypes } = FailureDetailsEntity.GetDefaultFailureTypes();

    const columns = useMemo(
        (): IDetailsReportColumns[] => [
            {
                id: DetailsReportColumnsIdEnum.DATE_STOP,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.date_stop").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) =>
                    millTimezone
                        ? moment.utc(row.date_stop).tz(millTimezone).format("MMM D, YYYY")
                        : moment.utc(row.date_stop).format("MMM D, YYYY"),
                renderExpandedRow: (row) =>
                    millTimezone
                        ? moment.utc(row.start_date).tz(millTimezone).format("MMM D, YYYY")
                        : moment.utc(row.start_date).format("MMM D, YYYY"),
            },
            {
                id: DetailsReportColumnsIdEnum.TIME_STOP,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.time_stop").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: true,
                render: (row) => row.time_stop,
                renderExpandedRow: (row) =>
                    millTimezone
                        ? moment.utc(row.start_date).tz(millTimezone).format("h:mm A")
                        : moment.utc(row.start_date).format("h:mm A"),
            },
            {
                id: DetailsReportColumnsIdEnum.DURATION,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.duration_in_minutes").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) =>
                    typeof row.duration_in_minutes === "string" ? (
                        <Typography variant="subtitle1" className={classes.toBeDefined}>
                            {row.duration_in_minutes}
                        </Typography>
                    ) : (
                        getDowntimeDurationInHoursMinutes(row.duration_in_minutes)
                    ),
                renderExpandedRow: (row) => getDowntimeDurationInHoursMinutes(row.valid_time),
            },
            {
                id: DetailsReportColumnsIdEnum.SHIFT,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.shift").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) =>
                    Boolean(row.shift && row.shift_info && !row.multiple_shifts) ? (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={
                                <>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.start",
                                        )}
                                        : {row?.shift_info?.start_time}
                                    </Typography>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.end",
                                        )}
                                        : {row?.shift_info?.end_time}
                                    </Typography>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.days",
                                        )}
                                        :{" "}
                                        {row?.shift_info?.shift_days
                                            .filter((shift_day) => shift_day.is_enabled)
                                            .map(
                                                (shift_day) =>
                                                    `${translateDefaultEntity(
                                                        i18n.language,
                                                        shift_day.day,
                                                        "name",
                                                        DefaultTranslationFieldsInTable.DAY,
                                                    )} `,
                                            )}
                                    </Typography>
                                </>
                            }
                        >
                            <span>{row.shift}</span>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={
                                <Typography className={classes.tooltipTexts}>
                                    {t("locations.mill_dashboards.details.tooltip_multiple_shifts_message")}
                                </Typography>
                            }
                        >
                            <span>{row.multiple_shifts_names}</span>
                        </Tooltip>
                    ),
                renderExpandedRow: (row) =>
                    row.shift &&
                    row.shift_info && (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={
                                <>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.start",
                                        )}
                                        : {row.shift_info.start_time}
                                    </Typography>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.end",
                                        )}
                                        : {row.shift_info.end_time}
                                    </Typography>
                                    <Typography className={classes.tooltipTexts}>
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.days",
                                        )}
                                        :{" "}
                                        {row.shift_info.shift_days
                                            .filter((shift_day) => shift_day.is_enabled)
                                            .map(
                                                (shift_day) =>
                                                    `${translateDefaultEntity(
                                                        i18n.language,
                                                        shift_day.day,
                                                        "name",
                                                        DefaultTranslationFieldsInTable.DAY,
                                                    )} `,
                                            )}
                                    </Typography>
                                </>
                            }
                        >
                            <span>{row.shift}</span>
                        </Tooltip>
                    ),
            },
            {
                id: DetailsReportColumnsIdEnum.PRODUCTION_LINE,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.production_line").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) => row.production_line,
            },
            {
                id: DetailsReportColumnsIdEnum.MACHINE_CENTER,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.machine_center").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                hideSortIcon: false,
                render: (row) => row.machine_center,
            },
            {
                id: DetailsReportColumnsIdEnum.FAILURE_TYPE,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.failure_type").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? (
                        row.event_information.failure_type.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ? (
                            <FailureTypeDetailsDropdown row={row.event_information} />
                        ) : (
                            t("locations.mill_dashboards.details.not_applicable")
                        )
                    ) : (
                        row.failure_type
                    );
                },
            },
            {
                id: DetailsReportColumnsIdEnum.AREA,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.area").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? (
                        row.event_information.failure_type.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ? (
                            <AreasDropdown row={row.event_information} />
                        ) : (
                            t("locations.mill_dashboards.details.not_applicable")
                        )
                    ) : (
                        row.area
                    );
                },
            },
            {
                id: DetailsReportColumnsIdEnum.FUNCTIONAL_LOCATION,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.functional_location").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? (
                        row.event_information.failure_type.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ? (
                            <FunctionalLocationsDropdown row={row.event_information} />
                        ) : (
                            t("locations.mill_dashboards.details.not_applicable")
                        )
                    ) : (
                        row.functional_location
                    );
                },
            },
            {
                id: DetailsReportColumnsIdEnum.FAILURE_CODE,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.failure_code").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? (
                        row.event_information.failure_type.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER ? (
                            <FailureCodeDetailsDropdown
                                defaultFailureTypes={defaultFailureTypes}
                                row={row.event_information}
                            />
                        ) : (
                            t("locations.mill_dashboards.details.not_applicable")
                        )
                    ) : (
                        row.failure_code
                    );
                },
            },
            {
                id: DetailsReportColumnsIdEnum.RCA,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.rca_triggered").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) => row.rca_triggered,
            },
            {
                id: DetailsReportColumnsIdEnum.OPERATOR,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.operator").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? <OperatorDropdown row={row.event_information} /> : row.operator;
                },
            },
            {
                id: DetailsReportColumnsIdEnum.COMMENTS,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_dashboards.details.comments").toUpperCase()}
                    </Typography>
                ),
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                hideSortIcon: Boolean(editionMode.length),
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return existingEdition ? (
                        <NoteTextfield row={row.event_information} />
                    ) : row.notes && row.notes.length > commentLengthLimit ? (
                        <Tooltip
                            PopperProps={{ className: classes.tooltip }}
                            title={<Typography className={classes.tooltipTexts}>{row.notes!}</Typography>}
                        >
                            <span>{truncateLongText(row.notes!, commentLengthLimit)}</span>
                        </Tooltip>
                    ) : (
                        <span>{row.notes}</span>
                    );
                },
            },
            {
                id: DetailsReportColumnsIdEnum.EDITION_OPTIONS,
                label: "",
                hideSortIcon: true,
                customStyles: cellStyle,
                headerCustomStyles: cellStyle,
                render: (row) => {
                    const existingEdition = editionMode.some(
                        (event) => event.id === row.event_information.event_report_id,
                    );

                    return (
                        <EditionOptionsCell
                            row={row.event_information}
                            editingRow={existingEdition}
                            setEditioMode={setEditioMode}
                            refetch={refetch}
                        />
                    );
                },
                renderExpandedRow: (row) => "",
            },
        ],
        [t, classes, millTimezone, i18n, defaultFailureTypes, editionMode, refetch],
    );

    return columns;
};
